import { 
  collection, 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc, 
  updateDoc, 
  getDocs, 
  query, 
  where,
  onSnapshot
} from 'firebase/firestore';
import { firebaseService } from './firebase.service.js';
import { FUNCTION_ENDPOINTS } from '../../config/firebase-functions.config.js';

// Device session configuration
const DEVICE_SESSION_CONFIG = {
  timeout: 8 * 60 * 60 * 1000, // 8 hours
  checkInterval: 5 * 60 * 1000, // Check every 5 minutes
  cleanupInterval: 60 * 60 * 1000, // Cleanup every hour
};

export interface DeviceSessionData {
  userId: string;
  deviceType: 'desktop' | 'mobile' | 'tablet';
  sessionId: string;
  loginTime: number;
  lastActivity: number;
  deviceInfo: {
    userAgent: string;
    platform: string;
    browser: string;
  };
  ipAddress?: string;
  rememberMe: boolean;
}

export interface DeviceInfo {
  userAgent: string;
  platform: string;
  browser: string;
}

export class DeviceSessionService {
  private db = firebaseService.getFirestore();
  private currentSessionId: string | null = null;
  private sessionCheckInterval: NodeJS.Timeout | null = null;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private sessionListeners: (() => void)[] = [];

  constructor() {
    this.startSessionCheck();
    this.startCleanupInterval();
  }

  // Create a new device session
  async createSession(deviceInfo: DeviceInfo, ipAddress?: string, rememberMe: boolean = false): Promise<DeviceSessionData> {
    try {
      const userId = firebaseService.getCurrentUser()?.uid;
      if (!userId) {
        throw new Error('User must be authenticated to create a device session');
      }

      const deviceType = this.getDeviceType();
      const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const sessionData: DeviceSessionData = {
        userId,
        deviceType,
        sessionId,
        loginTime: Date.now(),
        lastActivity: Date.now(),
        deviceInfo,
        ipAddress,
        rememberMe
      };

      // Use direct HTTP request to create session
      const response = await fetch(FUNCTION_ENDPOINTS.CREATE_DEVICE_SESSION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceType,
          deviceInfo,
          ipAddress,
          rememberMe
        })
      });

      const result = await response.json();

      if (result.success) {
        this.currentSessionId = sessionId;
        this.notifySessionListeners();
        return sessionData;
      } else {
        throw new Error('Failed to create device session');
      }
    } catch (error: any) {
      console.error('Error creating device session:', error);
      throw new Error(error.message || 'Failed to create device session');
    }
  }

  // Check for existing session on same device type
  async checkExistingSession(): Promise<DeviceSessionData | null> {
    try {
      const userId = firebaseService.getCurrentUser()?.uid;
      if (!userId) {
        return null;
      }

      const deviceType = this.getDeviceType();
      
      // Use direct HTTP request to get sessions
      const response = await fetch(FUNCTION_ENDPOINTS.GET_DEVICE_SESSIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      
      const result = await response.json();
      
      if (result.success && result.sessions) {
        const sessions = result.sessions as DeviceSessionData[];
        const existingSession = sessions.find(session => 
          session.deviceType === deviceType && 
          session.userId === userId &&
          this.isSessionActive(session)
        );
        
        return existingSession || null;
      }
      
      return null;
    } catch (error: any) {
      console.error('Error checking existing session:', error);
      return null;
    }
  }

  // Validate current session
  async validateSession(): Promise<boolean> {
    try {
      if (!this.currentSessionId) {
        return false;
      }

      const userId = firebaseService.getCurrentUser()?.uid;
      if (!userId) {
        return false;
      }

      // Use direct HTTP request to validate session
      const response = await fetch(FUNCTION_ENDPOINTS.UPDATE_DEVICE_SESSION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: this.currentSessionId
        })
      });

      const result = await response.json();
      return result.success;
    } catch (error: any) {
      console.error('Error validating session:', error);
      return false;
    }
  }

  // Delete current session
  async deleteCurrentSession(): Promise<void> {
    try {
      if (!this.currentSessionId) {
        return;
      }

      // Use direct HTTP request to delete session
      const response = await fetch(FUNCTION_ENDPOINTS.DELETE_DEVICE_SESSION, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: this.currentSessionId
        })
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error('Failed to delete session');
      }

      this.currentSessionId = null;
      this.notifySessionListeners();
    } catch (error: any) {
      console.error('Error deleting current session:', error);
      throw error;
    }
  }

  // Delete all user sessions
  async deleteAllUserSessions(): Promise<void> {
    try {
      // Use direct HTTP request to delete all sessions
      const response = await fetch(FUNCTION_ENDPOINTS.DELETE_ALL_USER_SESSIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });

      const result = await response.json();
      if (!result.success) {
        throw new Error('Failed to delete all sessions');
      }

      this.currentSessionId = null;
      this.notifySessionListeners();
    } catch (error: any) {
      console.error('Error deleting all user sessions:', error);
      throw error;
    }
  }

  // Get all user sessions
  async getUserSessions(): Promise<DeviceSessionData[]> {
    try {
      // Use direct HTTP request to get sessions
      const response = await fetch(FUNCTION_ENDPOINTS.GET_DEVICE_SESSIONS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });
      
      const result = await response.json();
      
      if (result.success && result.sessions) {
        return result.sessions as DeviceSessionData[];
      }
      
      return [];
    } catch (error: any) {
      console.error('Error getting user sessions:', error);
      return [];
    }
  }

  // Get device information
  getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent;
    const platform = this.getPlatform();
    const browser = this.getBrowser();

    return {
      userAgent,
      platform,
      browser
    };
  }

  // Detect device type
  getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Check for tablet first (tablets often report as mobile)
    if (/tablet|ipad|playbook|silk|kindle/.test(userAgent)) {
      return 'tablet';
    }
    
    // Check for mobile
    if (/mobile|android|iphone|ipod|blackberry|opera mini|iemobile/.test(userAgent)) {
      return 'mobile';
    }
    
    // Default to desktop
    return 'desktop';
  }

  // Add session change listener
  onSessionChange(callback: () => void): () => void {
    this.sessionListeners.push(callback);
    return () => {
      const index = this.sessionListeners.indexOf(callback);
      if (index > -1) {
        this.sessionListeners.splice(index, 1);
      }
    };
  }

  // Cleanup method
  destroy(): void {
    if (this.sessionCheckInterval) {
      clearInterval(this.sessionCheckInterval);
    }
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
    }
    this.sessionListeners = [];
  }

  // Private methods
  private getPlatform(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.indexOf('Win') !== -1) return 'Windows';
    if (userAgent.indexOf('Mac') !== -1) return 'MacOS';
    if (userAgent.indexOf('Linux') !== -1) return 'Linux';
    if (userAgent.indexOf('Android') !== -1) return 'Android';
    if (userAgent.indexOf('iPhone') !== -1) return 'iOS';
    if (userAgent.indexOf('iPad') !== -1) return 'iPadOS';
    
    return 'Unknown';
  }

  private getBrowser(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.indexOf('Chrome') !== -1 && userAgent.indexOf('Edg') === -1) return 'Chrome';
    if (userAgent.indexOf('Firefox') !== -1) return 'Firefox';
    if (userAgent.indexOf('Safari') !== -1 && userAgent.indexOf('Chrome') === -1) return 'Safari';
    if (userAgent.indexOf('Edg') !== -1) return 'Edge';
    if (userAgent.indexOf('Opera') !== -1) return 'Opera';
    
    return 'Unknown';
  }

  private isSessionActive(session: DeviceSessionData): boolean {
    const now = Date.now();
    const timeSinceLogin = now - session.loginTime;
    const timeSinceActivity = now - session.lastActivity;
    
    // Check if session is expired (8 hours max)
    if (timeSinceLogin > DEVICE_SESSION_CONFIG.timeout) {
      return false;
    }
    
    // Check if session is idle (30 minutes)
    if (timeSinceActivity > 30 * 60 * 1000) {
      return false;
    }
    
    return true;
  }

  private startSessionCheck(): void {
    this.sessionCheckInterval = setInterval(async () => {
      try {
        if (this.currentSessionId) {
          const isValid = await this.validateSession();
          if (!isValid) {
            this.currentSessionId = null;
            this.notifySessionListeners();
          }
        }
      } catch (error) {
        console.error('Error in session check:', error);
      }
    }, DEVICE_SESSION_CONFIG.checkInterval);
  }

  private startCleanupInterval(): void {
    this.cleanupInterval = setInterval(() => {
      // Cleanup is handled by Firebase scheduled function
      // This is just a placeholder for any client-side cleanup
    }, DEVICE_SESSION_CONFIG.cleanupInterval);
  }

  private notifySessionListeners(): void {
    this.sessionListeners.forEach(callback => callback());
  }
}

// Create and export singleton instance
export const deviceSessionService = new DeviceSessionService();
